import { memo } from "react";
import type { GfActionKey } from "../../../../types/gifting";
import { CAMPAIGN_TYPE_LABEL, STAGE_NAMES, tierClass } from "../../../../lib/giftingRules";
import { GfActionButton, GfAutoTag, GfSteps } from "../../ui/GfAction";
import { GfAvatar } from "../../ui/GfUi";
import type { Density, ListRow, RowGroup } from "./listModel";
import type { PipelineCtx } from "./model";
import { rowView } from "./rowView";

/** README ①e: テーブルの min-width */
export const LIST_MIN_WIDTH = 1270;

/** 一覧ボタンの文言（チップは短く、ボタンは動詞まで書く） */
const BUTTON_LABEL: Partial<Record<GfActionKey, string>> = {
  ship: "出荷指示を送る",
  remind: "リマインドを送る",
  addrnudge: "住所を催促",
  confirm: "投稿を確認",
};

interface RowProps {
  row: ListRow;
  ctx: PipelineCtx;
  compact: boolean;
  checked: boolean;
  open: boolean;
  onOpen: (id: string) => void;
  onToggle: (id: string) => void;
  onAction: (row: ListRow) => void;
}

const ListRowView = memo(function ListRowView({ row, ctx, compact, checked, open, onOpen, onToggle, onAction }: RowProps) {
  const { e, info, action } = row;
  const v = rowView(row, ctx);
  const wait = action.tier === 5;
  const shipped = e.stage === 5;
  const campType = row.campaign ? CAMPAIGN_TYPE_LABEL[row.campaign.type] : "";
  return (
    <div
      role="row"
      tabIndex={0}
      className={[
        "gf-lv2-row gf-lv2-grid",
        tierClass(action.tier),
        compact ? "is-compact" : "",
        wait ? "is-wait" : "",
        checked ? "is-checked" : "",
        open ? "is-open" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={() => onOpen(e.id)}
      onKeyDown={(ev) => {
        if (ev.target === ev.currentTarget && (ev.key === "Enter" || ev.key === " ")) {
          ev.preventDefault();
          onOpen(e.id);
        }
      }}
    >
      <button
        type="button"
        className={`gf-lv2-cb${checked ? " is-on" : ""}`}
        role="checkbox"
        aria-checked={checked}
        aria-label={`${info.creatorName}を選択`}
        onClick={(ev) => {
          ev.stopPropagation();
          onToggle(e.id);
        }}
      >
        {checked ? "✓" : ""}
      </button>

      <div className="gf-lv2-who">
        <GfAvatar name={info.creatorName} size={compact ? 20 : 28} />
        <div className="gf-lv2-who-text">
          <span className="gf-lv2-name">{info.creatorName}</span>
          <span className="gf-lv2-sub">{compact ? row.handle : [row.handle, row.primaryMedia].filter(Boolean).join(" · ")}</span>
        </div>
      </div>

      <div className="gf-lv2-camp">
        <span className="gf-lv2-camp-name" title={info.campaignName}>
          {info.campaignName}
        </span>
        {!compact && campType && <span className="gf-lv2-sub">{campType}</span>}
      </div>

      <div className="gf-lv2-stage">
        <GfSteps stage={e.stage} compact={compact} withLabel={false} />
        <span className="gf-lv2-stage-name">
          {STAGE_NAMES[e.stage]}
          {!compact && v.autoLabel && <GfAutoTag title={v.autoTitle ?? undefined}>自動·{v.autoLabel}</GfAutoTag>}
        </span>
      </div>

      <div className="gf-lv2-cell">
        <span className={`gf-lv2-main${v.contact.tone ? ` is-${v.contact.tone}` : ""}`}>{v.contact.c1}</span>
        {!compact && v.contact.c2 && <span className="gf-lv2-sub">{v.contact.c2}</span>}
      </div>

      <div className="gf-lv2-cell is-addr">
        <span className={`gf-lv2-addr is-${v.address.kind}`}>{v.address.text}</span>
        {!compact && v.address.sub && <span className="gf-lv2-sub2">{v.address.sub}</span>}
      </div>

      <div className="gf-lv2-cell">
        <span className={`gf-lv2-ship is-${v.ship.tone}`}>{v.ship.text}</span>
        {!compact && v.ship.sub && <span className="gf-lv2-sub2 gf-mono">{v.ship.sub}</span>}
      </div>

      <div className="gf-lv2-cell">
        <span className={`gf-lv2-post is-${v.post.tone}`}>{v.post.text}</span>
        {!compact && v.post.sub && <span className={`gf-lv2-sub2${v.post.warn ? " is-warn" : ""}`}>{v.post.sub}</span>}
      </div>

      <div className="gf-lv2-cell gf-lv2-dl">
        <span className={`gf-lv2-dl-main is-${v.deadline.tone}${v.deadline.big ? " is-big" : ""}${shipped ? " is-ship" : ""}`}>
          {v.deadline.text}
        </span>
        {!compact && v.deadline.sub && <span className="gf-lv2-sub2">{v.deadline.sub}</span>}
        {v.deadline.tag && <span className="gf-lv2-remtag">{v.deadline.tag}</span>}
      </div>

      <div className="gf-lv2-act">
        {wait ? (
          <span className="gf-lv2-wait">{e.stage >= 8 || e.status === "cancelled" ? "アーカイブ" : "待ち"}</span>
        ) : (
          <GfActionButton
            actionKey={action.key}
            label={BUTTON_LABEL[action.key]}
            compact={compact}
            onClick={(ev) => {
              ev.stopPropagation();
              onAction(row);
            }}
          />
        )}
      </div>

      <div className={`gf-lv2-age gf-mono${shipped ? " is-dim" : ""}`}>{v.elapsed}</div>
    </div>
  );
});

/** 案件一覧テーブル（README ①e）。sticky ヘッダ・グループ見出し（折りたたみ）・行 56px/34px */
export function ListTable({
  groups,
  grouped,
  ctx,
  density,
  selected,
  openId,
  collapsed,
  allChecked,
  someChecked,
  total,
  onOpen,
  onToggle,
  onToggleAll,
  onToggleGroup,
  onAction,
}: {
  groups: RowGroup[];
  grouped: boolean;
  ctx: PipelineCtx;
  density: Density;
  selected: ReadonlySet<string>;
  openId: string | null;
  collapsed: Record<string, boolean>;
  allChecked: boolean;
  someChecked: boolean;
  total: number;
  onOpen: (id: string) => void;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  onToggleGroup: (key: string) => void;
  onAction: (row: ListRow) => void;
}) {
  const compact = density === "compact";
  return (
    <div className="gf-lv2-table">
      <div className="gf-lv2-inner" style={{ minWidth: LIST_MIN_WIDTH }} role="table" aria-label="案件一覧">
        <div className="gf-lv2-head gf-lv2-grid" role="row">
          <button
            type="button"
            className={`gf-lv2-cb${allChecked ? " is-on" : someChecked ? " is-part" : ""}`}
            role="checkbox"
            aria-checked={allChecked ? true : someChecked ? "mixed" : false}
            aria-label="全て選択"
            onClick={onToggleAll}
          >
            {allChecked ? "✓" : someChecked ? "–" : ""}
          </button>
          <span>クリエイター</span>
          <span>案件</span>
          <span>段階</span>
          <span>連絡</span>
          <span>住所</span>
          <span>発送</span>
          <span>投稿</span>
          <span>制作期限</span>
          <span>次のアクション</span>
          <span>経過</span>
        </div>
        {total === 0 && <div className="gf-lv2-empty">該当する案件はありません</div>}
        {groups.map((g) => {
          const isOpen = !collapsed[g.key];
          return (
            <div key={g.key} className="gf-lv2-group">
              {grouped && (
                <button type="button" className="gf-lv2-ghead" aria-expanded={isOpen} onClick={() => onToggleGroup(g.key)}>
                  <span className="gf-lv2-ghead-in">
                    <span className="gf-lv2-caret">{isOpen ? "▾" : "▸"}</span>
                    <span className="gf-lv2-gname">{g.label}</span>
                    {g.sub && <span className="gf-lv2-gsub">{g.sub}</span>}
                    <span className="gf-mono gf-lv2-gcount">{g.rows.length}件</span>
                    {g.need > 0 && <span className="gf-lv2-gneed">要対応 {g.need}</span>}
                    {g.hot > 0 && <span className="gf-lv2-ghot">今日中 {g.hot}</span>}
                  </span>
                </button>
              )}
              {isOpen &&
                g.rows.map((r) => (
                  <ListRowView
                    key={r.e.id}
                    row={r}
                    ctx={ctx}
                    compact={compact}
                    checked={selected.has(r.e.id)}
                    open={openId === r.e.id}
                    onOpen={onOpen}
                    onToggle={onToggle}
                    onAction={onAction}
                  />
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
